import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, Mail, MessageSquare, Calendar, TrendingUp, 
  LogOut, Home, Bell, ChevronRight 
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import logo from '../images1/logo_L.png';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = logo;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const statCards = stats ? [
    {
      title: 'Total Subscribers',
      value: stats.total_subscribers,
      icon: Mail,
      color: 'bg-blue-500',
      link: '/admin/subscribers'
    },
    {
      title: 'Subscribers Contacted',
      value: stats.subscribers_contacted,
      icon: Users,
      color: 'bg-green-500',
      link: '/admin/subscribers'
    },
    {
      title: 'Pending Subscribers',
      value: stats.subscribers_pending,
      icon: Bell,
      color: 'bg-amber-500',
      link: '/admin/subscribers'
    },
    {
      title: 'Total Contacts',
      value: stats.total_contacts,
      icon: MessageSquare,
      color: 'bg-purple-500',
      link: '/admin/contacts'
    },
    {
      title: 'Contacts Handled',
      value: stats.contacts_contacted,
      icon: TrendingUp,
      color: 'bg-emerald-500',
      link: '/admin/contacts'
    },
    {
      title: 'Pending Follow-Ups',
      value: stats.contacts_pending,
      icon: Calendar,
      color: 'bg-red-500',
      link: '/admin/contacts'
    }
  ] : [];

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 w-64 h-full bg-slate-900 text-white z-40">
        <div className="p-6 border-b border-slate-800">
          <img src={LOGO_URL} alt="RENVITEK" className="h-10" />
          <p className="text-xs text-slate-400 mt-2">Admin Panel</p>
        </div>
        
        <nav className="p-4 space-y-2">
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-emerald-600 text-white"
            data-testid="nav-dashboard"
          >
            <Home size={20} />
            Dashboard
          </Link>
          <Link
            to="/admin/subscribers"
            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-800 text-slate-300 transition-colors"
            data-testid="nav-subscribers"
          >
            <Mail size={20} />
            Subscribers
          </Link>
          <Link
            to="/admin/contacts"
            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-800 text-slate-300 transition-colors"
            data-testid="nav-contacts"
          >
            <MessageSquare size={20} />
            Contacts
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
              {user?.username?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div>
              <p className="text-sm font-medium">{user?.username || 'Admin'}</p>
              <p className="text-xs text-slate-400">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            Dashboard
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Welcome back, {user?.username || 'Admin'}!
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="loading-spinner" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {statCards.map((card, index) => (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={card.link}>
                    <div className="stat-card group" data-testid={`stat-card-${index}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                            {card.title}
                          </p>
                          <p className="text-3xl font-bold text-slate-900 dark:text-white">
                            {card.value}
                          </p>
                        </div>
                        <div className={`w-12 h-12 ${card.color} rounded-full flex items-center justify-center`}>
                          <card.icon className="text-white" size={24} />
                        </div>
                      </div>
                      <div className="mt-4 flex items-center text-sm text-emerald-600 dark:text-emerald-400 group-hover:translate-x-1 transition-transform">
                        View Details <ChevronRight size={16} />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Follow-ups Alert */}
            {stats && (stats.followups_due_today > 0 || stats.followups_due_week > 0) && (
              <motion.div
                className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-400 mb-2 flex items-center gap-2">
                  <Bell className="text-amber-500" />
                  Follow-up Reminders
                </h3>
                <div className="flex gap-8">
                  <div>
                    <span className="text-3xl font-bold text-amber-600">{stats.followups_due_today}</span>
                    <p className="text-sm text-amber-700 dark:text-amber-300">Due Today</p>
                  </div>
                  <div>
                    <span className="text-3xl font-bold text-amber-600">{stats.followups_due_week}</span>
                    <p className="text-sm text-amber-700 dark:text-amber-300">Due This Week</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Quick Actions */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  <Link
                    to="/admin/subscribers"
                    className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                  >
                    <Mail className="text-blue-500" size={20} />
                    <span className="text-slate-700 dark:text-white">Manage Subscribers</span>
                  </Link>
                  <Link
                    to="/admin/contacts"
                    className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                  >
                    <MessageSquare className="text-purple-500" size={20} />
                    <span className="text-slate-700 dark:text-white">View Contact Requests</span>
                  </Link>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  System Info
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Last Login</span>
                    <span className="text-slate-700 dark:text-white">{new Date().toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">System Status</span>
                    <span className="text-emerald-500 font-medium">Online</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Version</span>
                    <span className="text-slate-700 dark:text-white">1.0.0</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
